import { useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import { message, Modal, Select, Table, TableProps } from "antd";
import Spinner from "../../components/shared/Spinner/Spinner";
import {
  OrderStatusType,
  ProductOrderssDataType,
} from "../../interfaces/admin.interface";
import { OrdersService } from "../../services/orders.service";
import { OrderStatusService } from "../../services/order_status.service";
import MainButton from "../../components/shared/MainButton/MainButton";
import { MdFileDownload } from "react-icons/md";
import { ExcelDowloadServices } from "../../services/downloadExcell.service";

const ProductOrders = () => {
  const [orders, setOrders] = useState<ProductOrderssDataType[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [isModalVisible, setIsModalVisible] = useState<boolean>(false);
  const [status, setStatus] = useState<OrderStatusType[]>([]);
  const [applyDetails, setApplyDetails] =
    useState<ProductOrderssDataType | null>(null);

  useEffect(() => {
    setLoading(true);
    OrdersService.getProductOrders()
      .then((response) => {
        // Safe check: response və data mövcuddurmu?
        if (Array.isArray(response?.data?.data)) {
          const dataWithKeys = response?.data?.data?.map(
            (item: ProductOrderssDataType, index: number) => ({
              ...item,
              key: index + 1,
            })
          );
          setOrders(dataWithKeys);
        }
      })
      .catch((err) => {
        console.error("Orders fetch error:", err);
      })
      .finally(() => {
        setLoading(false);
      });
  }, []);

  useEffect(() => {
    OrderStatusService.getAllStatus().then((response) => {
      if (Array.isArray(response?.data?.data)) {
        setStatus(response?.data?.data);
      }
    });
  }, []);

  const handleChangeStatus = async (orderId: number, statusId: number) => {
    try {
      const response = await OrdersService.changeProductsOrderStatus(
        orderId,
        statusId
      );
      if (response?.status === 200) {
        const updatedOrders = orders.map((order) =>
          order.id === orderId
            ? {
                ...order,
                status: {
                  ...order?.status, // Safe spread
                  id: statusId,
                  // Safe find: status massivi və ya tapılan element null ola bilər
                  name: status?.find((s) => s.id === statusId)?.name || "",
                },
              }
            : order
        );
        setOrders(updatedOrders);
        message.success(
          response?.data?.message || "Status uğurla dəyişdirildi"
        );
      }
    } catch (error: any) {
      // Safe error message: Xəta obyektinin içindəki data null ola bilər
      const errorMsg = error?.response?.data?.message || "Xəta baş verdi";
      message.error(errorMsg);
    }
  };

  const columns: TableProps<ProductOrderssDataType>["columns"] = [
    {
      title: "№",
      dataIndex: "key",
      sorter: (a, b) => (a.key || 0) - (b.key || 0), // Key yoxdursa 0 götür
    },
    {
      title: "Sifariş kodu",
      dataIndex: "order_code",
      render: (text) => text || "-", // Boşdursa tire qoy
    },
    {
      title: "Müştəri",
      dataIndex: "full_name",
      sorter: (a, b) => (a.full_name || "").localeCompare(b.full_name || ""),
      render: (text) => text || "-",
    },
    {
      title: "Nömrə",
      dataIndex: "number",
      render: (_, record) => {
        // Safe number display
        const prefix = record?.number_prefix || "";
        const num = record?.number || "";
        const fullNumber = `${prefix}${num}`;

        return fullNumber ? (
          <a className="content" href={`tel:${fullNumber}`}>
            {prefix} {num}
          </a>
        ) : (
          "-"
        );
      },
    },
    {
      title: "Ünvan",
      dataIndex: "address",
      render: (text) => text || "-",
    },
    {
      title: "Filial",
      dataIndex: "filial",
      // CRITICAL FIX: record.filial null ola bilər
      render: (_, record) => (
        <p className="status">{record?.filial?.name || "-"}</p>
      ),
    },
    {
      title: "Status",
      dataIndex: "status",
      render: (_, record) => (
        <Select
          // Safe value: record.status null ola bilər
          value={record?.status?.id}
          style={{ width: "150px" }}
          // Safe map: status massivi boş ola bilər
          options={status?.map((el) => ({
            label: el?.name || "",
            value: el?.id,
          }))}
          onChange={(value) => handleChangeStatus(record?.id, value)}
        />
      ),
    },
    {
      title: "Məhsul kodu",
      dataIndex: "",
      render: (_, record) => (
        <div className="">
          {/* Safe map: products null ola bilər */}
          {record?.products?.length > 0
            ? record?.products?.map((product, index) => (
                <p key={index}>{product?.model_code || "-"}</p>
              ))
            : "-"}
        </div>
      ),
    },
    {
      title: "Məhsul qiyməti",
      dataIndex: "",
      render: (_, record) => (
        <div>
          {/* Safe map and Safe price access */}
          {record?.products?.length > 0
            ? record?.products?.map((product, index) => (
                <p key={index}>
                  {product?.price ? `${product.price} AZN` : "-"}
                </p>
              ))
            : "-"}
        </div>
      ),
    },
    {
      title: "Ləğv edilmə səbəbi",
      dataIndex: "reason",
      render: (_, record) => <p className="status">{record?.reason || "-"}</p>,
    },
    {
      title: "Sifariş tarixi",
      dataIndex: "created_at",
      render: (_, record) => <p>{record?.created_at || "-"}</p>,
    },
  ];

  const handleDetail = (record: ProductOrderssDataType) => {
    setIsModalVisible(true);
    setApplyDetails(record);
  };

  const handleDownload = async () => {
    try {
      const res = await ExcelDowloadServices.getProductExcell();
      if (res?.data) {
        const blob = new Blob([res.data], { type: "application/vnd.ms-excel" });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", "product_orders.xls");
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      }
    } catch (err) {
      console.error("Download error", err);
    }
  };

  return (
    <div id="pages">
      <Helmet>
        <title>1412 | Məhsul sifarişləri</title>
      </Helmet>
      {loading ? (
        <Spinner />
      ) : (
        <div>
          <div className="page_head">
            <h3 className="page_title"> Məhsul sifarişləri</h3>
          </div>
          <div className="pages_components">
            <MainButton onClick={handleDownload}>
              <MdFileDownload size={25} />
            </MainButton>
            <div className="pages_table">
              <Table
                columns={columns}
                dataSource={orders}
                // Safe rowKey: id olmasa index istifadə etsin (fallback)
                rowKey={(record, index) =>
                  record?.id?.toString() || index?.toString() || "0"
                }
                pagination={false}
              />
            </div>
          </div>
        </div>
      )}
      <Modal
        title={"Məhsullar"}
        open={isModalVisible}
        onCancel={() => setIsModalVisible(false)}
        footer={null}
        style={{ minWidth: "340px" }}
        width={"100%"}
        centered
      >
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: "10px",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {/* Safe map inside Modal */}
          {applyDetails?.products?.map((el, index) => {
            return (
              <div key={index}>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                  }}
                >
                  <img
                    style={{ width: "50%", objectFit: "cover" }}
                    // Safe image source: Şəkil yoxdursa boş string
                    src={el?.cover_image || ""}
                    alt={el?.name || "Product"}
                  />
                </div>
                {/* Safe name display */}
                <p style={{ textAlign: "center" }}>{el?.name || "-"}</p>
              </div>
            );
          })}
        </div>
      </Modal>
    </div>
  );
};

export default ProductOrders;
